import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import locationSvg from 'assets/icons/location.svg';
import tagSvg from 'assets/icons/tag.svg';
import poisSvg from 'assets/icons/pois.svg';
import routeFrameListSvg from 'assets/icons/route_frame_lists.svg';
import formatsSvg from 'assets/icons/formats.svg';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';

interface ObjectiveFilterRecord {
  label: string;
  svg: string;
  onClick: VoidFunction;
  included: number;
  excluded: number;
  isDisabled?: boolean;
}

interface UseObjectiveFilters {
  filledFilters: ObjectiveFilterRecord[];
  emptyFilters: ObjectiveFilterRecord[];
}

const useObjectiveFilters = (): UseObjectiveFilters => {
  const dispatch = useDispatch();

  const locations = useSelector((store: Store) => store.objective.data.locations);
  const formats = useSelector((store: Store) => store.objective.data.formats);
  const tags = useSelector((store: Store) => store.objective.data.tags);
  const frameList = useSelector((store: Store) => store.objective.data.frameList);
  const poi = useSelector((store: Store) => store.objective.data.poi);

  const calculateIncludedExcluded = (data: CodeNameModel[]): { included: number; excluded: number } => {
    const included = data.filter((value) => value.include).length;

    return {
      included,
      excluded: data.length - included,
    };
  };

  const locationIncludedExcluded = useMemo(() => calculateIncludedExcluded(locations), [locations]);

  const formatsIncludedExcluded = useMemo(() => calculateIncludedExcluded(formats), [formats]);

  const tagsIncludedExcluded = useMemo(() => calculateIncludedExcluded(tags), [tags]);

  const poiIncludedExcluded = useMemo(() => calculateIncludedExcluded(poi), [poi]);

  const frameListIncludedExcluded = useMemo(() => {
    const data = [];

    data.push(...frameList.routeFrameCodes);

    frameList.listFiles.forEach(({ fileContents = [] }) => {
      data.push(...fileContents);
    });

    return calculateIncludedExcluded(data);
  }, [frameList]);

  const filtersData = useMemo(
    () => ({
      location: {
        label: 'Location',
        svg: locationSvg,
        onClick: () => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE_LOCATIONS)),
        ...locationIncludedExcluded,
      },
      format: {
        label: 'Format',
        svg: formatsSvg,
        onClick: () => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE_FORMAT)),
        ...formatsIncludedExcluded,
      },
      poi: {
        label: 'POI',
        svg: poisSvg,
        onClick: () => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE_POI)),
        ...poiIncludedExcluded,
      },
      tags: {
        label: 'Tags',
        svg: tagSvg,
        onClick: () => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE_TAGS)),
        ...tagsIncludedExcluded,
      },
      frameList: {
        label: 'Frame List',
        svg: routeFrameListSvg,
        onClick: () => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE_FRAME_LIST)),
        ...frameListIncludedExcluded,
      },
    }),
    [locationIncludedExcluded, formatsIncludedExcluded, tagsIncludedExcluded, frameListIncludedExcluded],
  );

  return useMemo(
    () =>
      Object.values(filtersData).reduce(
        (acc: UseObjectiveFilters, curr) => {
          if (curr.included || curr.excluded) acc.filledFilters.push(curr);
          else acc.emptyFilters.push(curr);

          return acc;
        },
        { filledFilters: [], emptyFilters: [] },
      ),
    [filtersData],
  );
};

export default useObjectiveFilters;
